/**
 * Intégrations d'agenda et d'encaissement.
 *
 * Ensembles fermés partagés entre la base (pgEnum), le serveur (zod) et le front (libellés i18n).
 * Règle : valeurs ASCII en snake_case, jamais de libellé humain ici. Ce fichier est réexporté par
 * `../enums.js`, qui reste le seul point d'entrée : personne n'importe un domaine directement.
 */

/**
 * Les trois fournisseurs du périmètre luxembourgeois, portés par un seul modèle générique. La V1
 * en avait trois générations concurrentes — `doctena_config` (41 colonnes), `docalia_config`,
 * puis `external_sync_entities` — plus une table de configuration par fournisseur
 * (`payconiq_config`, `medsmart_config`), toutes de la même forme {identifiants, actif, dates,
 * compteurs} (`docs/audit/audit-schema.md` §2.1).
 *
 * `buckaroo` encaisse les patients par le QR de facture Wero ; il a remplacé Payconiq le
 * 24 septembre 2026, Payconiq cessant au Luxembourg le 30 septembre (migration `0060`).
 */
export const integrationProviders = ['buckaroo', 'doctena', 'docalia'] as const;
export type IntegrationProvider = (typeof integrationProviders)[number];

/**
 * À qui appartient la connexion Doctena d'un cabinet. `organization` : un seul compte pour tout le
 * cabinet, réglé par le propriétaire — le comportement d'origine, et le défaut. `member` : chaque
 * praticien connecte son propre compte, et sa synchronisation n'alimente que son agenda.
 */
export const doctenaConnectionModes = ['organization', 'member'] as const;
export type DoctenaConnectionMode = (typeof doctenaConnectionModes)[number];

/**
 * Fournisseurs qui nous adressent des rappels signés. C'est un ensemble **plus large** que celui
 * des intégrations de cabinet, et il le restera : Stripe ne s'active pas cabinet par cabinet, il
 * prélève les cabinets pour leur abonnement. L'ajouter à `integrationProviders` l'aurait fait
 * apparaître comme une intégration configurable dans les réglages d'un praticien, ce qu'il n'est
 * pas. Deux ensembles, deux domaines.
 */
export const webhookProviders = [...integrationProviders, 'stripe'] as const;
export type WebhookProvider = (typeof webhookProviders)[number];

/**
 * Nature de l'objet local relié à un objet distant, pour les plateformes d'agenda. Un encaissement
 * Buckaroo n'a pas de lien : la vente est rattachée par `payments.provider_ref`, et le QR de
 * facture n'existe pas chez le fournisseur avant d'être scanné.
 */
export const externalEntityTypes = ['patient', 'appointment', 'professional'] as const;
export type ExternalEntityType = (typeof externalEntityTypes)[number];

/** Côté où l'objet est né. Départage les conflits d'une synchronisation bidirectionnelle. */
export const externalLinkOrigins = ['local', 'remote'] as const;
export type ExternalLinkOrigin = (typeof externalLinkOrigins)[number];

export const syncDirections = ['push', 'pull'] as const;
export type SyncDirection = (typeof syncDirections)[number];

export const syncJobStatuses = ['pending', 'running', 'succeeded', 'failed'] as const;
export type SyncJobStatus = (typeof syncJobStatuses)[number];

/**
 * Sort d'un événement de webhook. `ignored` désigne un rejeu déjà traité : il répond 200 pour que
 * le fournisseur cesse de réémettre, sans rien rejouer.
 */
export const webhookEventStatuses = ['received', 'processed', 'ignored', 'failed'] as const;
export type WebhookEventStatus = (typeof webhookEventStatuses)[number];
